import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button, { ButtonSize, buttonTheme } from "@components/button/button";
import Callout from "@components/callout/callout";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import Layout from "@components/layout/layout";
import DeprecatedSelect, { Select } from "@components/select/select";
import { Tooltip } from "@components/tooltip/tooltip";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import { sanitizeMarkdown } from "@helpers/string";

const StyleGuide = () => {
  const options = [
    { label: "Option 1", value: "option1" },
    { label: "Option 2", value: "option2" },
  ];

  const { options: userOptions, query, setQuery } = useUserComboboxQuery({});

  const currentUser = currentUserVar();
  const [inputValue, setInputValue] = useState("");
  return (
    <Layout>
      <Layout.Header
        breadcrumbs={[{ title: "Style Guide", url: "/styleguide" }]}
      />
      <Layout.Container>
        <Layout.Main width="default">
          <Layout.MainSection title="Markdown">
            <Layout.MainSubSection title="Default">
              <div className="flex flex-col gap-4 prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {sanitizeMarkdown(
                    "| Name  | Age |\n|-------|-----|\n| Alice |  30 |\n| Bob   |  25 |"
                  )}
                </ReactMarkdown>
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>

          <Layout.MainSection title="Tooltip">
            <Layout.MainSubSection title="Default">
              <div className="flex items-center gap-4">
                <Tooltip text="This is a tooltip">
                  <div>Hover me</div>
                </Tooltip>
                <Tooltip text="<div>Test</div><div>Test</div>">
                  <div>Tooltip with html</div>
                </Tooltip>
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>
          <Layout.MainSection title="Input">
            <Layout.MainSubSection title="Default">
              <div className="flex items-center gap-4">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type something..."
                  className="w-48"
                />
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>

          <Layout.MainSection title="InputDatePicker">
            <Layout.MainSubSection title="Default">
              <div className="flex flex-col gap-4">
                <div className="w-48">
                  <InputDatePicker date={new Date()} onChangeDate={() => {}} />
                </div>
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>
          <Layout.MainSection title="Button">
            <Layout.MainSubSection title="Themes">
              <div className="flex items-center gap-4">
                <Button text="Default" />
                <Button
                  text="buttonTheme.primary"
                  theme={buttonTheme.primary}
                />
                <Button text="buttonTheme.text" theme={buttonTheme.text} />
                <Button
                  text="buttonTheme.lightBlue"
                  theme={buttonTheme.lightBlue}
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Sizes">
              <div className="flex items-center gap-4">
                <Button text="ButtonSize.small" size={ButtonSize.small} />
                <Button text="ButtonSize.medium" size={ButtonSize.medium} />
                <Button
                  text="ButtonSize.large (Default)"
                  size={ButtonSize.large}
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Icon">
              <div className="flex items-center gap-4">
                <Button
                  icon={getTopicflowIcon("edit")}
                  theme={buttonTheme.iconGray}
                />
                <Button icon={getTopicflowIcon("edit")} />
                <Button
                  icon={getTopicflowIcon("edit")}
                  size={ButtonSize.small}
                />
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>
          <Layout.MainSection title="Select">
            <Layout.MainSubSection title="Default">
              <div className="flex items-center gap-4">
                <Select
                  options={options}
                  value={options[0].value}
                  onChange={() => {}}
                />
                <Select
                  options={[
                    ...options,
                    {
                      label:
                        "Truncated option label with a very long text and the limited width",
                      value: "selected",
                    },
                  ]}
                  value={"selected"}
                  onChange={() => {}}
                  className="w-[150px]"
                />
                <Select
                  options={[
                    ...options,
                    { label: "Disabled", value: "disabled" },
                  ]}
                  value={"disabled"}
                  onChange={() => {}}
                  disabled
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Theme">
              <div className="flex items-center gap-4">
                <Select
                  options={[
                    ...options,
                    { label: "buttonTheme.default", value: "default" },
                  ]}
                  value={"default"}
                  onChange={() => {}}
                />
                <Select
                  options={[
                    ...options,
                    { label: "buttonTheme.text", value: "text" },
                  ]}
                  value={"text"}
                  onChange={() => {}}
                  buttonProps={{
                    theme: buttonTheme.text,
                  }}
                />
                <Select
                  options={[
                    ...options,
                    { label: "buttonTheme.lightBlue", value: "lightBlue" },
                  ]}
                  value={"lightBlue"}
                  onChange={() => {}}
                  buttonProps={{
                    size: ButtonSize.large,
                    theme: buttonTheme.lightBlue,
                  }}
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Sizes">
              <div className="flex items-center gap-4">
                <Select
                  options={[
                    ...options,
                    { label: "ButtonSize.small", value: "small" },
                  ]}
                  value={"small"}
                  onChange={() => {}}
                  buttonProps={{
                    size: ButtonSize.small,
                  }}
                />
                <Select
                  options={[
                    ...options,
                    { label: "ButtonSize.medium", value: "medium" },
                  ]}
                  value={"medium"}
                  onChange={() => {}}
                  buttonProps={{
                    size: ButtonSize.medium,
                  }}
                />
                <Select
                  options={[
                    ...options,
                    { label: "ButtonSize.large", value: "large" },
                  ]}
                  value={"large"}
                  onChange={() => {}}
                  buttonProps={{
                    size: ButtonSize.large,
                  }}
                />
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>
          <Layout.MainSection title="DeprecatedSelect">
            <div>We are trying to replace DeprecatedSelect by Select.</div>
            <div className="flex items-center gap-4">
              <DeprecatedSelect
                options={options}
                value={options[0].value}
                onChange={() => {}}
              />
            </div>
          </Layout.MainSection>
          <Layout.MainSection title="ComboboxGeneric">
            <Layout.MainSubSection title="Default">
              <div className="flex items-center gap-4">
                <ComboboxGeneric
                  options={options}
                  value={options[0]}
                  onChangeValue={() => {}}
                />

                <ComboboxGeneric
                  options={[...options]}
                  value={null}
                  placeholder="Placeholder: search an option..."
                  onChangeValue={() => {}}
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Clearable">
              <div className="flex items-center gap-4">
                <ComboboxGeneric
                  options={options}
                  value={options[0]}
                  onChangeValue={() => {}}
                  clearable
                  onClearValue={() => {}}
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Fixed width">
              <div className="flex items-center gap-4">
                <ComboboxGeneric
                  options={[
                    ...options,
                    { label: "200px wide", value: "option3" },
                  ]}
                  value={{ label: "200px wide", value: "option3" }}
                  onChangeValue={() => {}}
                  className="w-[200px]"
                />
                <div className="w-[150px]">
                  <ComboboxGeneric
                    options={[
                      ...options,
                      {
                        label:
                          "Truncated option label with a very long text and the limited width",
                        value: "selected",
                      },
                    ]}
                    value={{
                      value: "truncated",
                      label:
                        "Truncated option label with a very long text and the limited width",
                    }}
                    className="w-[200px]"
                    onChangeValue={() => {}}
                  />
                </div>
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Using ComboboxGeneric.Button">
              <div className="flex items-center gap-4">
                <ComboboxGeneric
                  options={options}
                  value={options[0]}
                  onChangeValue={() => {}}
                >
                  <ComboboxGeneric.Button
                    size={ButtonSize.medium}
                    text="Call to action"
                  />
                </ComboboxGeneric>
                <ComboboxGeneric
                  options={options}
                  value={options[0]}
                  onChangeValue={() => {}}
                >
                  <ComboboxGeneric.Button
                    icon={getTopicflowIcon("plus")}
                    theme={buttonTheme.iconGray}
                  />
                </ComboboxGeneric>
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>
          <Layout.MainSection title="UserCombobox">
            <Layout.MainSubSection title="Default">
              <div className="flex items-center gap-4">
                <UserCombobox
                  query={query}
                  onChangeQuery={setQuery}
                  options={userOptions}
                  value={userOptions[0]}
                  onChangeValue={() => {}}
                  onClearValue={() => {}}
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Sizes">
              <div className="flex items-center gap-4">
                <UserCombobox
                  query={query}
                  onChangeQuery={setQuery}
                  options={userOptions}
                  value={userOptions[0]}
                  onChangeValue={() => {}}
                  onClearValue={() => {}}
                  buttonProps={{
                    size: ButtonSize.small,
                  }}
                />
                <UserCombobox
                  query={query}
                  onChangeQuery={setQuery}
                  options={userOptions}
                  value={userOptions[0]}
                  onChangeValue={() => {}}
                  onClearValue={() => {}}
                  buttonProps={{
                    size: ButtonSize.medium,
                  }}
                />
                <UserCombobox
                  query={query}
                  onChangeQuery={setQuery}
                  options={userOptions}
                  value={userOptions[0]}
                  onChangeValue={() => {}}
                  onClearValue={() => {}}
                  buttonProps={{
                    size: ButtonSize.large,
                  }}
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Clearable">
              <div className="flex items-center gap-4">
                <UserCombobox
                  query={query}
                  onChangeQuery={setQuery}
                  options={userOptions}
                  value={userOptions[0]}
                  onChangeValue={() => {}}
                  onClearValue={() => {}}
                  clearable
                />
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Using UserCombobox.Button">
              <div className="flex items-center gap-4">
                <UserCombobox
                  query={query}
                  onChangeQuery={setQuery}
                  options={userOptions}
                  value={userOptions[0]}
                  onChangeValue={() => {}}
                  onClearValue={() => {}}
                >
                  <UserCombobox.Button text="Call to action" />
                </UserCombobox>
                <UserCombobox
                  query={query}
                  onChangeQuery={setQuery}
                  options={userOptions}
                  value={userOptions[0]}
                  onChangeValue={() => {}}
                  onClearValue={() => {}}
                >
                  <UserCombobox.Button square>
                    <Avatar user={currentUser} size="4" />
                  </UserCombobox.Button>
                </UserCombobox>
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>

          <Layout.MainSection title="Callout">
            <Layout.MainSubSection title="Default">
              <div className="flex flex-col gap-4">
                <Callout>This is a test</Callout>
                <Callout icon="info">This is a test</Callout>

                <Callout title="This is the title">
                  This is the description
                </Callout>
                <Callout title="This is the title" icon="info">
                  This is the description
                </Callout>

                <Callout title="This is the title" center>
                  This is the description
                </Callout>
                <Callout title="This is the title" icon="info" center>
                  This is the description
                </Callout>
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Centered">
              <div className="flex flex-col gap-4">
                <Callout center>This is a test</Callout>

                <Callout title="This is the title" center>
                  This is the description
                </Callout>
                <Callout title="This is the title" icon="info" center>
                  This is the description
                </Callout>
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Theme">
              <div className="flex flex-col gap-4">
                <Callout title="This is the title" icon="info" theme="default">
                  Default theme
                </Callout>
                <Callout title="This is the title" icon="info" theme="warning">
                  Warning theme
                </Callout>
                <Callout title="This is the title" icon="info" theme="success">
                  Success theme
                </Callout>
              </div>
            </Layout.MainSubSection>
            <Layout.MainSubSection title="Size">
              <div className="flex flex-col gap-4">
                <Callout title="This is the title" icon="info" theme="default">
                  Default size
                </Callout>
                <Callout
                  title="This is the title"
                  icon="info"
                  theme="default"
                  size="small"
                >
                  Small size
                </Callout>
              </div>
            </Layout.MainSubSection>
          </Layout.MainSection>
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default StyleGuide;
